import React, { useState, useRef } from "react";
import {
    Box,
    Button,
    TextField,
    IconButton,
    Typography,
    Alert,
    Collapse
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import ConfirmDialog from "../../Common/ConfirmDialog";

// MicroWell supports up to 3 calibration levels (MAX_MW_LEVELS = 3).
// Each level can have multiple ALU replicates, entered as a
// space/comma-separated list in the Replicates (ALU) column.
const MAX_CAL_LEVELS = 3;

export default function MicroWellCalibrationData({ rows, setRows }) {

    const [textArea, setTextArea] = useState("");
    const [errors, setErrors] = useState([]);
    const [showError, setShowError] = useState(true);
    const fileInputRef = useRef(null);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const createEmptyRows = (count = 2) =>
        Array.from({ length: count }, () => ({ conc: "", resp: "" }));

    const safeRows = rows?.length ? rows : createEmptyRows(2);

    const addRow = () => {
        if (safeRows.length >= MAX_CAL_LEVELS) return;
        setRows(prev => [...prev, { conc: "", resp: "" }]);
    };

    const removeRow = (index) => {
        setRows(prev => {
            if (prev.length <= 1) return [{ conc: "", resp: "" }];
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    };

    const handleRowChange = (index, field, value) => {
        const updated = [...safeRows];
        updated[index] = { ...updated[index], [field]: value };
        setRows(updated);
    };

    const resetGrid = () => {
        setRows(createEmptyRows(2));
    };

    const clearTextarea = () => {
        setTextArea("");
        setErrors([]);
        setShowError(false);
        setRows(createEmptyRows(2));
    };

    // Parse pasted text: each line is "concentration  rep1 rep2 ..."
    // or "concentration,rep1,rep2,...". Up to MAX_CAL_LEVELS lines are read.
    const handleParseText = (content) => {
        setErrors([]);
        setShowError(true);
        const text = content !== undefined ? content : textArea;

        if (!text.trim()) {
            setErrors(["Values are empty. Please enter or upload data."]);
            return;
        }

        const lines = text.split("\n").filter(l => l.trim() !== "");
        const tempErrors = [];
        const parsed = [];

        lines.forEach((line, index) => {
            if (/concentration|response|alu/i.test(line)) return;
            const cols = line.trim().split(/[\t ,]+/).filter(c => c !== "");
            if (cols.length < 2) {
                tempErrors.push(`Line ${index + 1} is invalid: "${line}"`);
                return;
            }
            const conc = cols[0];
            const reps = cols.slice(1).join(" ");
            if (isNaN(Number(conc))) {
                tempErrors.push(`Line ${index + 1}: concentration "${conc}" is not a number.`);
                return;
            }
            parsed.push({ conc, resp: reps });
        });

        if (tempErrors.length > 0) {
            setErrors(tempErrors);
        }

        if (parsed.length > 0) {
            const limited = parsed.slice(0, MAX_CAL_LEVELS);
            setRows(limited.length < 1 ? createEmptyRows(2) : limited);
        }
    };

    const processFile = (file) => {
        if (!file) return;
        const ext = file.name.split(".").pop().toLowerCase();
        if (!["csv", "txt", "xlsx", "xls"].includes(ext)) {
            setErrors(["Unsupported file type. Please use TXT, CSV, XLS, or XLSX."]);
            setShowError(true);
            return;
        }

        if (ext === "xlsx" || ext === "xls") {
            const reader = new FileReader();
            reader.onload = (e) => {
                const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const json = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
                const text = json.map(r => r.join("\t")).join("\n");
                setTextArea(text);
                handleParseText(text);
            };
            reader.readAsArrayBuffer(file);
        } else {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                setTextArea(text);
                handleParseText(text);
            };
            reader.readAsText(file);
        }
    };

    const handleFileChange = (e) => {
        processFile(e.target.files?.[0]);
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        processFile(e.dataTransfer.files?.[0]);
    };

    return (
        <Box>
            {/* Drag-and-drop / paste zone */}
            <Box
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                sx={{
                    border: dragActive ? "2px dashed #774def" : "2px dashed #ccc",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: dragActive ? "#f3f0ff" : "transparent",
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.2s"
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <UploadFileIcon sx={{ color: "#774def", mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                    Drag & drop a file here, or click to upload (CSV, TXT, XLSX)
                </Typography>
            </Box>
            <input
                type="file"
                accept=".xlsx,.xls,.csv,.txt"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
            />

            {/* Paste text box */}
            <TextField
                multiline
                minRows={3}
                fullWidth
                placeholder={`Paste calibration data here.\nEach line: concentration  rep1 rep2 ...\nExample:\n0    100  102  98\n50   510  498  503\n200  980  975`}
                value={textArea}
                onChange={(e) => setTextArea(e.target.value)}
                sx={{ mb: 1, fontFamily: "monospace" }}
                size="small"
            />

            <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => handleParseText()}
                >
                    Parse
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={<ClearIcon />}
                    onClick={clearTextarea}
                >
                    Clear
                </Button>
            </Box>

            <Collapse in={showError && errors.length > 0}>
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <IconButton size="small" onClick={() => setShowError(false)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    {errors.map((e, i) => <div key={i}>{e}</div>)}
                </Alert>
            </Collapse>

            {/* Cal level rows */}
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                MicroWell supports up to {MAX_CAL_LEVELS} calibration levels. Enter concentration and ALU replicates (space or comma separated) for each level.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography sx={{ width: 30, fontSize: 12, fontWeight: 600 }}>#</Typography>
                    <Typography sx={{ width: 120, fontSize: 12, fontWeight: 600 }}>Concentration</Typography>
                    <Typography sx={{ flex: 1, fontSize: 12, fontWeight: 600 }}>Replicates (ALU)</Typography>
                    <Box sx={{ width: 36 }} />
                </Box>

                {safeRows.map((row, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Typography sx={{ width: 30, fontSize: 12, color: "text.secondary" }}>
                            {index + 1}
                        </Typography>
                        <TextField
                            size="small"
                            value={row.conc}
                            onChange={(e) => handleRowChange(index, "conc", e.target.value)}
                            placeholder="0"
                            sx={{ width: 120 }}
                            type="number"
                        />
                        <TextField
                            size="small"
                            value={row.resp}
                            onChange={(e) => handleRowChange(index, "resp", e.target.value)}
                            placeholder="e.g. 100 102 98"
                            sx={{ flex: 1 }}
                        />
                        <IconButton
                            size="small"
                            onClick={() => removeRow(index)}
                            disabled={safeRows.length <= 1}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addRow}
                    disabled={safeRows.length >= MAX_CAL_LEVELS}
                >
                    Add Level
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={<RestartAltIcon />}
                    onClick={() => setOpenResetDialog(true)}
                >
                    Reset
                </Button>
            </Box>

            <ConfirmDialog
                open={openResetDialog}
                title="Reset Calibration Data"
                message="This will clear all calibration levels. Continue?"
                onConfirm={() => { resetGrid(); setOpenResetDialog(false); }}
                onCancel={() => setOpenResetDialog(false)}
            />
        </Box>
    );
}
